import type { FC } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import ConfigContext from './common/ConfigContext';
import PeriodContext from './common/PeriodContext';
import Blocks from './components/Blocks';
import Layout from './components/Layout';
import LoadingContainer from './components/LoadingContainer';
import { useSubscription } from './hooks/useSubscription';
import { loadEntity, updateEntityData } from './utils/entities/all_entities';
import { ERROR_UNKNOWN } from './constants/errors';
import ErrorBoundary from './common/ErrorBoundary';
import DisplayContext from './common/DisplayContext';
import ThemeContext from './common/ThemeContext';
import OpponentsHeader from './components/DesignVersion/Grad/OpponentsHeader';
import Tabs from './components/Tabs';
import CircleLoader from './components/CircleLoader/CircleLoader';
import appStyle from './app.style.module.scss';
import { getOriginUrl } from './utils/api_url';

const App: FC = () => {
  const originUrl = getOriginUrl();

  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const { entity, tab, id } = config;
  const { period } = useContext(PeriodContext);
  const displayState = useContext(DisplayContext);
  const { designVersion } = useContext(ThemeContext);
  const { displayedBlocks, displayedTab, setDisplayedTab, singleBlockDisplay } = displayState;

  const cantDoRequests = !originUrl || !entity || !entity || id === undefined;

  const {
    data: entityData,
    status: requestStatus,
    errorType
  } = useSubscription(
    (currentData: ComposedEntityData | undefined) =>
      loadEntity(config, displayedBlocks, displayedTab, period, currentData),
    [period, displayedBlocks],
    {
      prevent: cantDoRequests
    },
    updateEntityData
  );

  const usesTabs = designVersion === 'grad' && tab.length > 0;

  return (
    <Layout>
      <ErrorBoundary>
        <LoadingContainer
          status={requestStatus}
          data={entityData}
          error={errorType || (cantDoRequests ? ERROR_UNKNOWN : null)}
          firstLoadIndicatorOnly
          sendWidgetDisplayMetrics
          // loader={
          //   <div className={appStyle.tabLoadingContainer}>
          //   <CircleLoader size={20} />
          //   </div>
          // }
        >
          {designVersion === 'grad' && !singleBlockDisplay && !displayedBlocks.includes('name') && (
            <OpponentsHeader data={entityData!} />
          )}
          {usesTabs && (
            <Tabs
              title={t('tabs-title')}
              tabs={tab}
              selectTab={setDisplayedTab}
              selectedTab={displayedTab}
              translationKey="tabs"
            />
          )}
          {!usesTabs || requestStatus !== 'LOADING' ? (
            <Blocks blocks={displayedBlocks} entityType={entity!} data={entityData!} />
          ) : (
            <div className={appStyle.tabLoadingContainer}>
              <CircleLoader size={20} />
            </div>
          )}
        </LoadingContainer>
      </ErrorBoundary>
    </Layout>
  );
};

App.displayName = 'App';

export default App;
